import { isNil } from "lodash-es";
import React from "react";

import { getCancelTokenSource, IHttpCancelTokenSource, IHttpRequestConfig } from "@/browser";

export interface BuilderRendererCommon {
  file?: File;
  attachment?: any;
}

export interface IAttachmentRenderer extends BuilderRendererCommon {
  progress?: number;
  error?: Error;

  onRetry?: () => void;
}

export interface BuilderProps extends BuilderRendererCommon {
  onUpload?: (value: any) => void;
  onError?: (error: Error) => void;
}

export interface State {
  progress: number;
  error?: Error;
}

export function prepareAttachment<T>(
  upload: (props: T & { onUploadProgress: (progress: number) => void }, options: IHttpRequestConfig) => Promise<any>,
) {
  return function <P extends IAttachmentRenderer>(WrappedComponent: React.ComponentType<P>) {
    type Props = BuilderProps & P & T;

    return class AttachmentBuilder extends React.PureComponent<Props, State> {
      state: State = {
        progress: 0,
      };

      source: IHttpCancelTokenSource | null = null;

      componentDidMount() {
        const { file } = this.props;
        if (file) {
          this.upload();
        }
      }

      componentWillUnmount() {
        if (!isNil(this.source)) {
          this.source.cancel();
        }
      }

      componentDidUpdate(prevProps: Props) {
        if (this.props.file && this.props.file !== prevProps.file) {
          this.upload();
        }
      }

      upload = async () => {
        try {
          if (!isNil(this.source)) {
            this.source.cancel();
          }

          this.source = getCancelTokenSource();

          const config = {
            cancelToken: this.source.token,
          };

          const attachment = await upload(
            {
              ...this.props,
              onUploadProgress: progress => this.setState({ progress }),
            },
            config,
          );

          this.handleUpload(attachment);
        } catch (error) {
          this.setState({ error }, () => this.handleError(error));
        }
      };

      handleRetry = () => {
        this.setState({ progress: 0, error: undefined }, this.upload);
      };

      handleError = (error: Error) => {
        const { onError } = this.props;
        if (onError) {
          onError(error);
        }
      };

      handleUpload = (value: any) => {
        const { onUpload } = this.props;
        if (onUpload) {
          onUpload(value);
        }
      };

      render() {
        const { onUpload, onError, ...rest } = this.props as any;

        return <WrappedComponent {...rest} {...this.state} onRetry={this.handleRetry} />;
      }
    };
  };
}
